import { CORE_API_URL } from '../config';
import { authService } from './authService';

// Client for the Signal Sources API (services/data-pipeline cmd/api, slice V4).
//
// Same bearer token the rest of the app carries — the Go API validates it against
// auth.session exactly as the Python API does, so no second sign-in is involved.
//
// Design: docs/superpowers/specs/2026-07-29-signal-sources-v5-page-design.md

const authHeaders = () => {
  const token = authService.requireToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
};

async function request(path, options = {}) {
  const response = await fetch(`${CORE_API_URL}${path}`, { headers: authHeaders(), ...options });

  // An expired session bounces to login rather than surfacing as an error banner.
  // Without this the page rendered "not signed in" twice and sat there: every read
  // fails, every panel shows the same message, and nothing tells the user the fix
  // is to sign in again. handleSessionExpired is guarded against concurrent 401s,
  // so the page's parallel reads still produce exactly one redirect.
  //
  // ONLY 401. A 500 is not an auth problem, and logging someone out because a
  // server had a bad minute would destroy their session for no reason.
  if (response.status === 401) {
    authService.handleSessionExpired();
    throw new Error('Session expired — please sign in again.');
  }
  if (!response.ok) {
    // The Go API answers with {detail: "..."}, matching the Python API's shape, so
    // callers keep one error contract across both.
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || `Request failed (${response.status})`);
  }
  return response.status === 204 ? null : response.json();
}

const signalSourcesService = {
  // getPage returns { companies, counts }. counts describe the WHOLE tenant, never
  // the current page, so the summary line stays stable while you filter or scroll.
  getPage({ show = 'monitoring', limit = 50, offset = 0 } = {}) {
    const qs = new URLSearchParams({ show, limit: String(limit), offset: String(offset) });
    return request(`/api/signal-sources?${qs}`);
  },

  // getProposals returns { proposals, counts } — ONE ranked list. The limit is
  // applied AFTER ranking by the API, so a small page still holds the best
  // candidates whether or not we hold a page for them.
  //
  // counts splits the backlog by ask (watch / needs_page) for reporting only. They
  // are the FULL sizes, not the page length, so the UI can say "25 of 426" rather
  // than implying the list is exhausted.
  getProposals({ limit = 25 } = {}) {
    return request(`/api/signal-sources/proposals?limit=${limit}`);
  },

  // getEvidence returns the records behind ONE reason on ONE proposal: the job rows
  // behind "24 roles on your books here", the people behind "11 of your people work
  // there". total is the real count while rows is capped, so the dialog can say
  // "showing 50 of 423" instead of silently truncating.
  //
  // A reason whose evidence is already its own sentence (recorded_url,
  // job_outside_seed_window) answers 400 — the caller should not offer a drill-down
  // for those. Use REASONS_WITH_EVIDENCE to decide.
  getEvidence({ companyId, code, limit = 50, offset = 0 }) {
    const qs = new URLSearchParams({
      company_id: companyId, code, limit: String(limit), offset: String(offset),
    });
    return request(`/api/signal-sources/evidence?${qs}`);
  },

  // decide re-posts the payload AND the pages from the RENDERED row rather than
  // rebuilding them, so accepting acts on exactly what the user was shown.
  //
  // pages is what makes a bundle one round trip. Sending the company without them
  // would switch monitoring on with nothing to watch — the dead end this redesign
  // removes — so it always travels, defaulting to [] rather than being omitted.
  decide({ kind, dedupeKey, action, payload, pages = [] }) {
    return request('/api/signal-sources/proposals/decide', {
      method: 'POST',
      body: JSON.stringify({ kind, dedupe_key: dedupeKey, action, payload, pages }),
    });
  },

  // setMonitoring never writes signals_on directly — the API pairs it with the
  // dismissal stamp that keeps the seeding scan safe to re-run.
  setMonitoring(companyId, signalsOn) {
    return request(`/api/companies/${companyId}/monitoring`, {
      method: 'PATCH',
      body: JSON.stringify({ signals_on: signalsOn }),
    });
  },

  // searchCompanies backs the add-a-source dialog's picker. It covers the whole
  // book, not just monitored companies — adding a page switches monitoring on, so
  // the old "monitored only" rule described a constraint that no longer exists.
  //
  // Each match carries suggested_url: a page already on the company's record that
  // is NOT yet a source, which the dialog offers as placeholder text. Empty when
  // every page we hold is already registered, or was dismissed.
  searchCompanies(q, limit = 20) {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    return request(`/api/companies/search?${qs}`);
  },

  // getCompany reads ONE company's rationale and page state, for the confirm shown
  // before switching it on. has_page distinguishes "we hold nothing" from "we hold
  // a page and it is already a source" — only the first should demand a URL.
  getCompany(companyId) {
    return request(`/api/companies/${companyId}`);
  },

  addSource({ companyId, url, sourceType, note = '' }) {
    return request('/api/signal-sources', {
      method: 'POST',
      body: JSON.stringify({ company_id: companyId, url, source_type: sourceType, note }),
    });
  },

  // patch carries only the fields being changed: { is_active?, source_type?, note? }.
  patchSource(sourceId, patch) {
    return request(`/api/signal-sources/${sourceId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  removeSource(sourceId) {
    return request(`/api/signal-sources/${sourceId}`, { method: 'DELETE' });
  },
};

export default signalSourcesService;
